import { useCallback, useEffect, useRef } from "react";

const useScrollSelectedIntoView = ({
    selectedId,
    enabled = true,
    behavior = "smooth",
    block = "nearest",
    inline = "nearest",
    onScrolled,
} = {}) => {
    const elementRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastScrolledIdRef = useRef("");
    const scrollTargetId =
        selectedId === undefined || selectedId === null
            ? ""
            : String(selectedId).trim();

    const scrollCurrentElement = useCallback(() => {
        if (typeof window === "undefined") return;
        if (!enabled || !scrollTargetId || !elementRef.current) return;
        if (lastScrolledIdRef.current === scrollTargetId) return;

        if (animationFrameRef.current) {
            window.cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = window.requestAnimationFrame(() => {
            elementRef.current?.scrollIntoView({
                behavior,
                block,
                inline,
            });
            lastScrolledIdRef.current = scrollTargetId;
            animationFrameRef.current = null;
            onScrolled?.(scrollTargetId);
        });
    }, [behavior, block, enabled, inline, onScrolled, scrollTargetId]);

    const selectedElementRef = useCallback(
        (element) => {
            elementRef.current = element;
            scrollCurrentElement();
        },
        [scrollCurrentElement],
    );

    useEffect(() => {
        scrollCurrentElement();
    }, [scrollCurrentElement]);

    useEffect(() => {
        if (!enabled) {
            elementRef.current = null;
            lastScrolledIdRef.current = "";
        }
    }, [enabled]);

    useEffect(
        () => () => {
            if (animationFrameRef.current) {
                window.cancelAnimationFrame(animationFrameRef.current);
            }
        },
        [],
    );

    return selectedElementRef;
};

export default useScrollSelectedIntoView;
